import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Icon from './icons/Icon';
import SEO from './SEO';
import { resolveIcon } from './icons/iconRegistry';
import { SITE_URL } from '../config/site';
import { trackServiceDetailView } from '../utils/analytics';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { serviceId } = useParams();

  useEffect(() => {
    if (serviceId) {
      trackServiceDetailView(serviceId);
    }
  }, [serviceId]);

  const services = {
    'general-checkup': {
      title: 'General Dentistry',
      icon: 'stethoscope',
      description: 'Regular dental examinations to maintain optimal oral health and catch potential issues early.',
      details: [
        'Comprehensive oral examination',
        'X-rays when necessary',
        'Oral cancer screening',
        'Gum disease assessment',
        'Professional consultation',
        'Treatment planning'
      ],
      duration: '30-45 minutes',
      frequency: 'Every 6 months recommended'
    },
    'teeth-cleaning': {
      title: 'Teeth Cleaning',
      icon: 'sparkles',
      description: 'Professional cleaning to remove plaque, tartar, and stains for a healthier, brighter smile.',
      details: [
        'Plaque and tartar removal',
        'Teeth polishing',
        'Flossing',
        'Fluoride treatment',
        'Stain removal',
        'Oral hygiene education'
      ],
      duration: '45-60 minutes',
      frequency: 'Every 6 months recommended'
    },
    'cosmetic-dentistry': {
      title: 'Cosmetic Dentistry',
      icon: 'gem',
      description: 'Transform your smile with advanced cosmetic treatments including whitening, veneers, and smile makeovers.',
      details: [
        'Teeth whitening',
        'Dental veneers',
        'Composite bonding',
        'Smile design',
        'Gum contouring',
        'Crowns and bridges'
      ],
      duration: 'Varies (30 minutes - 2 hours)',
      frequency: 'As needed'
    },
    'orthodontics': {
      title: 'Orthodontics',
      icon: 'wrench',
      description: 'Comprehensive orthodontic treatment including braces and aligners to achieve a perfectly aligned smile.',
      details: [
        'Traditional braces',
        'Clear aligners',
        'Retainers',
        'Orthodontic consultation',
        'Progress monitoring',
        'Treatment planning'
      ],
      duration: 'Initial consultation: 1 hour',
      frequency: 'Monthly adjustments'
    },
    'dental-implants': {
      title: 'Dental Implants',
      icon: 'bone',
      description: 'Durable and natural-looking dental implants to restore missing teeth and improve oral function.',
      details: [
        'Implant consultation',
        'Surgical placement',
        'Abutment attachment',
        'Crown placement',
        'Follow-up care',
        'Long-term maintenance'
      ],
      duration: 'Multiple visits over 3-6 months',
      frequency: 'As needed for replacement'
    },
    'teeth-whitening': {
      title: 'Teeth Whitening',
      icon: 'smile',
      description: 'Professional teeth whitening treatments to brighten your smile and boost your confidence.',
      details: [
        'In-office whitening',
        'Take-home whitening kits',
        'Stain removal',
        'Custom trays',
        'Maintenance guidance',
        'Results consultation'
      ],
      duration: '1-2 hours (in-office)',
      frequency: 'As needed'
    },
    'emergency-care': {
      title: 'Emergency Dental Care',
      icon: 'siren',
      description: 'Immediate dental care for urgent situations including pain, trauma, and dental emergencies.',
      details: [
        'Toothache relief',
        'Broken tooth repair',
        'Knocked-out tooth treatment',
        'Dental trauma care',
        'Abscess treatment',
        'Temporary repairs'
      ],
      duration: '30 minutes - 2 hours',
      frequency: 'Emergency basis'
    }
  };

  const service = services[serviceId];

  if (!service) {
    return (
      <div className="service-detail-page">
        <SEO
          title="Service Not Found | Dr. Muyabins Dental Care"
          description="The requested dental service could not be found."
          url={`${SITE_URL}/services/${serviceId}`}
          noindex
        />
        <div className="service-not-found">
          <h2>Service Not Found</h2>
          <p>The service you're looking for doesn't exist.</p>
          <Link to="/services" className="btn-back-services">Back to Services</Link>
        </div>
      </div>
    );
  }

  const iconName = resolveIcon(service.icon, serviceId);

  return (
    <div className="service-detail-page">
      <SEO
        title={`${service.title} in Dar es Salaam | Dr. Muyabins Dental Care`}
        description={`${service.description} Book an appointment for ${service.title.toLowerCase()} in Dar es Salaam, Tanzania.`}
        keywords={`${service.title}, dentist Dar es Salaam, dental clinic Tanzania`}
        url={`${SITE_URL}/services/${serviceId}`}
      />
      <div className="service-detail-container">
        <Link to="/services" className="back-link">
          ← Back to Services
        </Link>

        <div className="service-detail-header">
          <div className="service-detail-icon">
            <Icon name={iconName} size={48} />
          </div>
          <div>
            <h1>{service.title}</h1>
            <p className="service-detail-description">{service.description}</p>
          </div>
        </div>

        <div className="service-detail-content">
          <div className="service-detail-main">
            <div className="service-info-card">
              <h3>What's Included</h3>
              <ul className="service-details-list">
                {service.details.map((detail, index) => (
                  <li key={index}>{detail}</li>
                ))}
              </ul>
            </div>

            <div className="service-info-card">
              <h3>About This Service</h3>
              <p>
                {service.description} Our team uses the latest techniques and equipment 
                to ensure the best possible results for your dental health and appearance.
              </p>
            </div>
          </div>

          <div className="service-detail-sidebar">
            <div className="service-info-box">
              <div className="info-box-icon"><Icon name="timer" size={24} /></div>
              <div>
                <h4>Duration</h4>
                <p>{service.duration}</p>
              </div>
            </div>

            <div className="service-info-box">
              <div className="info-box-icon"><Icon name="calendar" size={24} /></div>
              <div>
                <h4>Frequency</h4>
                <p>{service.frequency}</p>
              </div>
            </div>

            <div className="service-info-box">
              <div className="info-box-icon"><Icon name="dollarSign" size={24} /></div>
              <div>
                <h4>Pricing</h4>
                <p>Contact us for pricing information</p>
              </div>
            </div>

            <Link to="/appointment" className="btn-book-service">
              Book Appointment
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
